import React, { useState, useEffect, useMemo } from 'react';
import { purchasePaymentsService } from '../services/purchasePaymentsService';
import { suppliersService } from '../services/suppliersService';
import { Button, Badge, Card, Input, Select, Icon } from '../components/ui';
import { IconPercent, IconSync, IconPrinter, IconFilter, IconX, IconInfoCircle } from '@tabler/icons-react';

const normalizeList = (data) => Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const formatCurrency = (a) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(a);

export default function PurchaseRSPage() {
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', supplierId: '', amountMin: '', amountMax: '' });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [paymentsData, suppliersData] = await Promise.all([
        purchasePaymentsService.list(),
        suppliersService.list(),
      ]);
      const allPayments = normalizeList(paymentsData);
      // Garder seulement les RS achats
      setPayments(allPayments.filter(p => p.method === 'WITHHOLDING_TAX'));
      setSuppliers(normalizeList(suppliersData));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const supplierById = useMemo(() => {
    const map = new Map();
    suppliers.forEach(s => map.set(s._id || s.id, s));
    return map;
  }, [suppliers]);

  const resetFilters = () => setFilters({ dateFrom: '', dateTo: '', supplierId: '', amountMin: '', amountMax: '' });

  const filteredPayments = useMemo(() => payments.filter(p => {
    if (filters.supplierId && p.supplierId !== filters.supplierId) return false;
    if (filters.dateFrom) { const f = new Date(filters.dateFrom); f.setHours(0,0,0,0); if (new Date(p.paymentDate) < f) return false; }
    if (filters.dateTo) { const t = new Date(filters.dateTo); t.setHours(23,59,59,999); if (new Date(p.paymentDate) > t) return false; }
    if (filters.amountMin !== '' && toNumber(p.amount) < parseFloat(filters.amountMin)) return false;
    if (filters.amountMax !== '' && toNumber(p.amount) > parseFloat(filters.amountMax)) return false;
    return true;
  }), [payments, filters]);

  const activeFiltersCount = [filters.dateFrom, filters.dateTo, filters.supplierId, filters.amountMin, filters.amountMax].filter(Boolean).length;
  const totalRS = useMemo(() => filteredPayments.reduce((s, p) => s + toNumber(p.amount), 0), [filteredPayments]);

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>État RS Achats</title><style>
      body{font-family:Arial,sans-serif;margin:30px}
      h1{color:#7c3aed;border-bottom:2px solid #7c3aed;padding-bottom:10px}
      .solde{background:#ede9fe;padding:15px;border-radius:8px;margin:20px 0;font-size:18px;font-weight:bold;color:#5b21b6}
      table{width:100%;border-collapse:collapse;margin-top:20px}
      th{background:#ede9fe;color:#5b21b6;padding:10px;text-align:left;border:1px solid #ddd6fe}
      td{padding:8px 10px;border:1px solid #e5e7eb;font-size:13px}
      tr:nth-child(even){background:#f5f3ff}
      .total{font-weight:bold;text-align:right;padding:15px;background:#ede9fe;font-size:16px}
    </style></head><body>
      <h1>ÉTAT DES RETENUES À LA SOURCE — ACHATS FOURNISSEURS</h1>
      <div class="solde">Total RS achats : ${formatCurrency(totalRS)}</div>
      <p><strong>Généré le :</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
      <table><thead><tr>
        <th>Date</th><th>Fournisseur</th><th>Référence</th><th>Statut</th><th style="text-align:right">Montant RS (DT)</th>
      </tr></thead><tbody>
      ${filteredPayments.map(p => {
        const supplier = supplierById.get(p.supplierId);
        return `<tr>
          <td>${p.paymentDate || '—'}</td>
          <td>${supplier?.name || '—'}</td>
          <td>${p.reference || '—'}</td>
          <td>${p.status || '—'}</td>
          <td style="text-align:right;color:#7c3aed;font-weight:bold">${toNumber(p.amount).toFixed(3)}</td>
        </tr>`;
      }).join('')}
      </tbody></table>
      <div class="total">Total : ${formatCurrency(totalRS)}</div>
    </body></html>`);
    w.document.close();
    w.print();
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-xl text-gray-600">Chargement...</div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Icon name="IconPercent" className="text-purple-600" size={28} />RS Achats Fournisseurs
          </h1>
          <p className="text-gray-500 mt-1">Suivi des retenues à la source sur achats fournisseurs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={loadData}>
            <Icon name="IconSync" size={16} className="mr-1" />Rafraîchir
          </Button>
          <Button variant="secondary" size="sm" onClick={handlePrint}>
            <Icon name="IconPrinter" size={16} className="mr-1" />Imprimer
          </Button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="border-l-4 border-purple-500 p-6">
          <div className="text-sm text-gray-500 mb-1">Total RS achats (période filtrée)</div>
          <div className="text-2xl font-bold text-purple-700">{formatCurrency(totalRS)}</div>
          <div className="text-xs text-gray-400 mt-1">Retenues dues aux fournisseurs</div>
        </Card>
        <Card className="border-l-4 border-blue-500 p-6">
          <div className="text-sm text-gray-500 mb-1">Nombre de règlements RS</div>
          <div className="text-2xl font-bold text-blue-700">{filteredPayments.length}</div>
          <div className="text-xs text-gray-400 mt-1">Sur {payments.length} au total</div>
        </Card>
        <Card className="border-l-4 border-green-500 p-6">
          <div className="text-sm text-gray-500 mb-1">Fournisseurs concernés</div>
          <div className="text-2xl font-bold text-green-700">
            {new Set(filteredPayments.map(p => p.supplierId)).size}
          </div>
          <div className="text-xs text-gray-400 mt-1">Fournisseurs avec RS</div>
        </Card>
      </div>

      {payments.length === 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <Icon name="IconInfoCircle" className="text-purple-600 shrink-0" size={24} />
          <div>
            <div className="font-semibold text-purple-800">Aucune retenue à la source achat</div>
            <div className="text-sm text-purple-700">
              Créez un règlement achat avec la méthode "Retenue à la source (RS)" pour le voir ici.
            </div>
          </div>
        </div>
      )}

      {/* TABLEAU */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-800">Historique des retenues achats</h2>
          <Button variant={showFilters || activeFiltersCount > 0 ? 'primary' : 'secondary'} size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Icon name="IconFilter" size={16} className="mr-1" /> Filtres
            {activeFiltersCount > 0 && <Badge variant="info">{activeFiltersCount}</Badge>}
          </Button>
        </div>

        {showFilters && (
          <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fournisseur</label>
                <Select value={filters.supplierId} onChange={e => setFilters({...filters, supplierId: e.target.value})}>
                  <option value="">-- Tous --</option>
                  {suppliers.map(s => {
                    const id = s._id || s.id;
                    return <option key={id} value={id}>{s.name} {s.mf ? `(${s.mf})` : ''}</option>;
                  })}
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date début</label>
                <Input type="date" value={filters.dateFrom} onChange={e => setFilters({...filters, dateFrom: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label>
                <Input type="date" value={filters.dateTo} onChange={e => setFilters({...filters, dateTo: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Montant min</label>
                <Input type="number" step="0.001" placeholder="0" value={filters.amountMin} onChange={e => setFilters({...filters, amountMin: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Montant max</label>
                <Input type="number" step="0.001" placeholder="∞" value={filters.amountMax} onChange={e => setFilters({...filters, amountMax: e.target.value})} />
              </div>
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <Icon name="IconX" size={16} className="mr-1" />Réinitialiser
                </Button>
              </div>
            </div>
            {activeFiltersCount > 0 && (
              <div className="mt-3 pt-3 border-t border-purple-200 text-xs text-purple-700 font-medium">
                {filteredPayments.length} résultat(s) sur {payments.length} — Total filtré : {formatCurrency(totalRS)}
              </div>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-purple-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-purple-800 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-purple-800 uppercase">Fournisseur</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-purple-800 uppercase">Référence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-purple-800 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-purple-800 uppercase">Notes</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-purple-800 uppercase">Montant RS (DT)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-400">
                    <Icon name="IconPercent" size={48} className="mb-3 block mx-auto opacity-30" />
                    {activeFiltersCount > 0 ? 'Aucune retenue ne correspond aux filtres' : 'Aucune retenue à la source achat enregistrée'}
                  </td>
                </tr>
              ) : filteredPayments.map(p => {
                const id = p._id || p.id;
                const supplier = supplierById.get(p.supplierId);
                return (
                  <tr key={id} className="hover:bg-purple-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-600">{p.paymentDate || '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">
                      {supplier?.name || '—'}
                      {supplier?.mf && <span className="text-xs text-gray-400 ml-1">({supplier.mf})</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {p.reference
                        ? <Badge variant="warning">{p.reference}</Badge>
                        : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge variant={
                        p.status === 'POSTED' ? 'success' :
                        p.status === 'DRAFT' ? 'gray' :
                        'danger'
                      }>{p.status || '—'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{p.notes || '—'}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-purple-700">
                      {toNumber(p.amount).toFixed(3)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filteredPayments.length > 0 && (
              <tfoot className="bg-purple-50 border-t-2 border-purple-200">
                <tr>
                  <td colSpan="5" className="px-4 py-3 text-sm font-semibold text-purple-800">
                    Total ({filteredPayments.length} retenue{filteredPayments.length > 1 ? 's' : ''})
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-purple-700 text-base">
                    {formatCurrency(totalRS)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
